import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type { DirectoryPharmacy, PharmacyLink } from '@curentia/shared';
import { ErrorText, Field, LockButton, Page, Submit } from '../components';
import { useLoad } from '../lib/useLoad';
import { useAction, useDevice, useSnapshot } from '../state';

/** A doctor's linked pharmacies, which receive their prescriptions directly, and the search to add one. */
export function Pharmacies() {
  const device = useDevice();
  const { practicesVersion } = useSnapshot();
  const [version, setVersion] = useState(0);
  const { data, error } = useLoad(() => device.api.pharmacyLinks(), [device, practicesVersion, version]);
  const changed = () => setVersion((n) => n + 1);
  const linked = new Set(data?.links.map((l) => l.pharmacy.id));

  return (
    <Page actions={<LockButton />}>
      <h1>Linked pharmacies</h1>
      <p className="muted">You can send prescriptions straight to a pharmacy you link. Each patient can still choose their own.</p>
      <section className="card" aria-labelledby="links-heading">
        <h2 id="links-heading">Your links</h2>
        <ErrorText error={error} />
        {data && data.links.length === 0 ? <p className="muted">You have not linked a pharmacy yet.</p> : null}
        {data && data.links.length > 0 ? (
          <ul className="list" aria-label="Your links">
            {data.links.map((link) => (
              <LinkRow key={link.id} link={link} onChange={changed} />
            ))}
          </ul>
        ) : null}
      </section>
      <Search linked={linked} onLinked={changed} />
      <div className="links">
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}

function LinkRow({ link, onChange }: { link: PharmacyLink; onChange: () => void }) {
  const device = useDevice();
  const { busy, error, run } = useAction();
  return (
    <li className="member" aria-label={link.pharmacy.name}>
      <div>
        <strong>{link.pharmacy.name}</strong>
        <div className="muted">
          {link.pharmacy.city} · {link.status === 'active' ? 'Linked' : 'Waiting for the pharmacy to accept'}
        </div>
      </div>
      <ErrorText error={error} />
      <button
        type="button"
        className="button secondary small"
        disabled={busy}
        onClick={() =>
          void run(async () => {
            await device.api.removePharmacyLink(link.id);
            onChange();
          })
        }
      >
        {link.status === 'active' ? 'Remove link' : 'Withdraw'}
      </button>
    </li>
  );
}

function Search({ linked, onLinked }: { linked: Set<string>; onLinked: () => void }) {
  const device = useDevice();
  const [city, setCity] = useState('');
  const [name, setName] = useState('');
  const [results, setResults] = useState<DirectoryPharmacy[] | null>(null);
  const search = useAction();
  const link = useAction();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (city.trim().length < 2) return search.setError('Enter the city.');
    void search.run(async () => {
      const { pharmacies } = await device.api.searchPharmacies(city.trim(), name.trim() || undefined);
      setResults(pharmacies);
    });
  };

  return (
    <section className="card" aria-labelledby="search-heading">
      <h2 id="search-heading">Find a pharmacy</h2>
      <form onSubmit={submit} noValidate>
        <Field label="City" value={city} onChange={(e) => setCity(e.target.value)} required />
        <Field label="Pharmacy name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
        <ErrorText error={search.error} />
        <Submit busy={search.busy} busyText="Searching…">
          Search
        </Submit>
      </form>
      {results && results.length === 0 ? <p className="muted">No verified pharmacy matches.</p> : null}
      {results && results.length > 0 ? (
        <ul className="list" aria-label="Pharmacies found">
          {results.map((pharmacy) => (
            <li key={pharmacy.id} className="member" aria-label={pharmacy.name}>
              <div>
                <strong>{pharmacy.name}</strong>
                <div className="muted">{pharmacy.city}</div>
              </div>
              {linked.has(pharmacy.id) ? (
                <span className="muted">Already linked</span>
              ) : (
                <button
                  type="button"
                  className="button small"
                  disabled={link.busy}
                  onClick={() =>
                    void link.run(async () => {
                      await device.api.requestPharmacyLink(pharmacy.id);
                      onLinked();
                    })
                  }
                >
                  Link {pharmacy.name}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      <ErrorText error={link.error} />
    </section>
  );
}
